import React, { useCallback } from "react";
import { useAppSelector, useAppDispatch } from "./hooks/reduxHooks";
import { useFeatureFlag } from "./hooks/useFeatureFlag";
import { useStreamingMessage } from "./hooks/useStreamingMessage";
import { selectResultsForTurn } from "./ResultsSlice_Store";
import { openDealDetail, fetchDealDetail } from "./DealsTableSlice_Store";
import { setLayoutMode } from "./LayoutSlice_Store";
import InlineDealsTable from "./InlineDealsTable_Component";
import type { Deal } from "./types/redux";

interface Props {
  turnId: string | undefined;
}

/**
 * Thin wrapper that encapsulates the hook calls needed to render an inline
 * deals table for a single chat turn.  Lives as its own component so hooks
 * are legal (can't call hooks inside a .map()).
 */
export default function InlineDealsTableForTurn({ turnId }: Props): JSX.Element | null {
  const dispatch = useAppDispatch();
  const flagOn = useFeatureFlag("inlineDeals");
  const turnResults = useAppSelector((state) => selectResultsForTurn(state, turnId));
  const streamingData = useStreamingMessage(turnId || "");

  const handleDealClick = useCallback((deal: Deal) => {
    const dealId = deal.dealId || deal.id;
    if (!dealId) return;
    dispatch(openDealDetail(dealId));
    dispatch(fetchDealDetail(dealId));
    dispatch(setLayoutMode("workspace"));
  }, [dispatch]);

  // Don't render until the message has finished streaming
  const isStreaming = !!streamingData.displayedText && !(streamingData.isComplete && !streamingData.isAnimating);

  if (!flagOn || !turnResults?.deals?.length || isStreaming) return null;

  return (
    <InlineDealsTable
      deals={turnResults.deals}
      documents={turnResults.documents}
      onDealClick={handleDealClick}
    />
  );
}
